<template>
  <component-wrapper
    :class="{
      'product-teasers-fi': true,
      'product-teasers-fi--light': light,
      'product-teasers-fi--white': white,
      'product-teasers-fi--expandable': expandable,
      'product-teasers-fi--content': showContent,
    }"
  >
    <grid-container-fi grid-class="fi-gap-zero md:fi-gap-zero lg:fi-gap-large">
      <div
        v-if="isSlider || currentFields.headline?.value || isEditMode"
        :class="{
          'product-teasers-fi__header': true,
          'product-teasers-fi__header--expandable': expandable,
        }"
      >
        <div
          :class="{
            'product-teasers-fi__products__head': true,
            'product-teasers-fi__products__head--expandable': expandable,
          }"
        >
          <div
            v-if="!isEditMode"
            class="product-teasers-fi__products__headline"
            data-test="product-teasers-headline"
          >
            {{ currentFields.headline?.value }}
          </div>
          <!-- eslint-disable vue/no-v-html -->
          <div
            v-if="isEditMode"
            class="product-teasers-fi__products__headline"
            v-html="currentFields.headline?.editable"
          />
          <!-- eslint-enable vue/no-v-html -->
        </div>
        <div
          v-if="isSlider && carouselRef !== null"
          class="product-teasers-fi__slider-controls"
        >
          <carousel-slider-controls-fi
            :carousel="carouselRef"
            :dark="white"
          />
        </div>
      </div>

      <div
        :class="{
          'product-teasers-fi__products': true,
          'product-teasers-fi__products--static': !expandable,
        }"
      >
        <div class="product-teasers-fi__products-wrapper">
          <carousel-fi
            v-if="catalogData?.length"
            :key="changesCount"
            ref="carouselRef"
            v-intersect.once="onIntersect"
            :class="{
              'product-teasers-fi__slider': true,
              'product-teasers-fi__slider--no-slider': !isSlider,
              'product-teasers-fi__slider--with-content': showContent,
            }"
            :settings="sliderSettings"
            theme="product-teasers"
            no-arrows
            clipped-right
            @splide-dragged="dragged = false"
            @splide-drag="dragged = true"
            @before-change="moved = true"
            @after-change="moved = false"
          >
            <carousel-slide-fi
              v-for="(item, index) in catalogData"
              :key="item.id"
              theme="product-teasers"
              :class="{
                'product-teasers-fi__slide': true,
                'product-teasers-fi__slide--expandable': expandable,
              }"
            >
              <div class="product-teasers-fi__slide-wrapper">
                <product-tile-fi
                  v-if="isVariant"
                  :product="item"
                  :best-price="bestPrices[item.id]"
                  :best-price-loading="bestPriceLoading"
                  :out-of-stock="retailerOutOfStock[index]"
                  :is-recommendation="isRecommendation"
                  :is-guided-recommandation="isGuidedRecommandation"
                  :expandable
                  :dragged
                  :moved
                  @product-added="$emit('product-added', $event)"
                  @tile-clicked="trackSelectItem(item, !isStandaloneVariant)"
                />
                <product-category-tile-fi
                  v-else
                  :category="item"
                  :is-guided-recommandation="isGuidedRecommandation"
                  @tile-clicked="trackSelectItem(item)"
                />
              </div>
            </carousel-slide-fi>
            <carousel-slide-fi
              v-if="expandable"
              theme="product-teasers"
              class="product-teasers-fi__slide product-teasers-fi__slide--expandable"
            >
              <div><!-- spacer --></div>
            </carousel-slide-fi>
          </carousel-fi>
        </div>
      </div>
      <div
        v-if="showContent"
        class="product-teasers-fi__content"
      >
        <div class="product-teasers-fi__content-container">
          <richtext-fi
            v-if="currentFields.content"
            class="product-teasers-fi__text"
            is-theme-light
            :html-content="currentFields.content"
          />
        </div>
      </div>
    </grid-container-fi>
  </component-wrapper>
</template>

<script setup lang="ts">
import { useQuery } from '@vue/apollo-composable';
import CarouselSliderControlsFi from 'atoms/carousel-fi/CarouselSliderControlsFi.vue';
import { CarouselFi, CarouselSlideFi, RichtextFi } from 'atoms/index';
import ComponentWrapper from 'components/component-wrapper/ComponentWrapper.vue';
import GridContainerFi from 'components/grid-fi/GridContainerFi.vue';
import ProductCategoryTileFi from 'components/product-category/product-category-tile/ProductCategoryTileFi.vue';
import ProductTileFi, { type AddProductData } from 'components/product-tile-fi/ProductTileFi.vue';
import { useBreakpoints } from 'composables/breakpoint';
import { useFischer } from 'composables/fischerPlugin';
import { useTracking } from 'composables/tracking';
import DUMMY from 'gql/dummy.gql';
import GETPRODUCTCATEGORYLISTBYID from 'gql/getProductCategoryListById.gql';
import GETPRODUCTLISTBYID from 'gql/getProductListById.gql';
import GET_RETAILER_AVAILABILITY_FOR_IDS from 'gql/getRetailerAvailabilityForIds.gql';
import screenWidths from 'tailwind/screenWidths';
import { computed, inject, onMounted, ref, useTemplateRef, watch, watchEffect } from 'vue';

import type {
  GetMultipleBestPricesQuery,
  GetMultipleBestPricesQueryVariables,
  GetProductCategoryListByIdQuery,
  GetProductCategoryListByIdQueryVariables,
  GetProductListByIdQuery,
  GetProductListByIdQueryVariables,
  GetRetailerAvailabilityForIdsQuery,
  GetRetailerAvailabilityForIdsQueryVariables,
} from '@/generated/graphql';
import type { CheckboxField, RichTextField, SingleLineTextField } from '@/sitecoreFieldTypes';
import { useNotificationStore } from '@/store/pinia/notification';

interface Fields {
  familyList?: SingleLineTextField;
  productList?: SingleLineTextField;
  hideContent?: CheckboxField;
  content?: RichTextField;
  headline?: SingleLineTextField;
}

const importGET_MULTIPLE_BEST_PRICES = () => import('gql/getMultipleBestPrices.gql');

defineOptions({
  name: 'ProductTeasersFi',
});

const fields = inject<Fields>('fields');

const props = withDefaults(
  defineProps<{
    customFields?: Fields;
    light?: boolean;
    white?: boolean;
    isRecommendation?: boolean;
    isGuidedRecommandation?: boolean;
    showVariantsCount?: boolean;
    expandable?: boolean;
  }>(),
  {
    customFields: () => ({}),
  },
);

defineEmits<{
  'product-added': [value: AddProductData];
}>();

const { isXL, isLG, isMD, isSM } = useBreakpoints();
const { trackViewItemList, trackSelectItem } = useTracking();
const fischer = useFischer();
const notificationStore = useNotificationStore();

const bestPriceLoading = ref(true);
const changesCount = ref(0);
const catalogData = ref<null | any[]>(null);
const GET_MULTIPLE_BEST_PRICES = ref(DUMMY);
const isMounted = ref(false);
const carouselRef = useTemplateRef<typeof CarouselFi>('carouselRef');
const dragged = ref(false);
const moved = ref(false);

const isStandaloneVariant = computed<boolean>(() => Object.keys(props.customFields).length === 0);

const currentFields = computed<Fields>(
  () => (isStandaloneVariant.value ? fields : props.customFields) || {},
);

const isVariant = computed<boolean>(() => Boolean(currentFields.value.productList));

const productIds = computed<string[]>(
  () => currentFields.value.productList?.value?.split('|') || [],
);

const familyIds = computed<string[]>(
  () =>
    (currentFields.value.familyList?.value && currentFields.value.familyList.value.split('|')) ||
    [],
);

const productQuery = computed(() =>
  productIds.value.map((id) => ({
    qty: 0,
    productCode: id,
  })),
);

const showContent = computed<boolean>(() => {
  if (props.isRecommendation || currentFields.value.hideContent?.value) {
    return false;
  }

  return Boolean(currentFields.value.content?.value) || fischer.app.isEditMode();
});

const sliderSettings = computed(() => ({
  rewind: false,
  pagination: false,
  perPage: 1,
  perMove: 1,
  destroy: false,
  gap: props.expandable ? 12 : 24,
  noDrag: 'input, .no-drag',
  autoWidth: props.expandable,
  breakpoints: props.expandable
    ? {
        [screenWidths.sm]: {
          gap: 24,
        },
      }
    : {
        [screenWidths.xs]: {
          perPage: 1,
        },
        [screenWidths.sm]: {
          perPage: 2,
        },
        [screenWidths.md]: {
          perPage: 3,
        },
        [screenWidths.lg]: {
          perPage: showContent.value ? 2 : 4,
        },
        [screenWidths.xl]: {
          perPage: showContent.value ? 3 : 5,
        },
      },
}));

const visibleSlidesCount = computed<number>(() => {
  if (isXL.value) {
    return showContent.value || props.expandable ? 3 : 5;
  }
  if (isLG.value) {
    return showContent.value || props.expandable ? 2 : 4;
  }
  if (isMD.value) {
    return props.expandable ? 2 : 3;
  }
  if (isSM.value) {
    return props.expandable ? 1 : 2;
  }
  return 1;
});

const isSlider = computed<boolean>(() => {
  if (!isMounted.value) {
    return false;
  }

  return Boolean(catalogData.value?.length && catalogData.value?.length > visibleSlidesCount.value);
});

const retailerOutOfStock = computed<Record<number, boolean>>(() =>
  (getRetailerAvailabilityForIds.value?.getRetailerAvailabilityForIds || []).map((availability) => {
    if (!availability) {
      // something went wrong, default to 'can search for retailers'
      return false;
    }
    return availability.stock === 'empty' || availability.stock === null;
  }),
);

watch(catalogData, () => {
  changesCount.value += 1;
});

const onQueryError = (...errorValue: any) => {
  console.error(...errorValue);
  notificationStore.openGeneralErrorNotification();
};

onMounted(() => {
  isMounted.value = true;
});

const loadPrices = computed<boolean>(() =>
  Boolean(
    productIds.value?.length &&
      fischer.shop.isClosedShop() &&
      !fischer.app.isFite() &&
      !fischer.app.isEditMode(),
  ),
);

watchEffect(() => {
  if (isMounted.value && loadPrices.value) {
    importGET_MULTIPLE_BEST_PRICES().then((imported) => {
      GET_MULTIPLE_BEST_PRICES.value = imported.default;
    });
  }
});

const { onResult: onProductCategoryListByIdResult, onError: onGetProductsCategoryListByIdError } =
  useQuery<GetProductCategoryListByIdQuery>(
    GETPRODUCTCATEGORYLISTBYID,
    computed<GetProductCategoryListByIdQueryVariables>(() => ({
      language: fischer.catalogLocale(),
      ids: familyIds.value.join(','),
      channel: fischer.brandId(),
    })),
    {
      errorPolicy: 'all',
      enabled: computed(() => familyIds.value.length > 0),
    },
  );

onProductCategoryListByIdResult(({ data }) => {
  if (data?.getProductCategoryListById) {
    catalogData.value = data.getProductCategoryListById;
  }
});

onGetProductsCategoryListByIdError(onQueryError);

const { onResult: onGetProductListByIdResult, onError: onGetProductListByIdError } =
  useQuery<GetProductListByIdQuery>(
    GETPRODUCTLISTBYID,
    computed<GetProductListByIdQueryVariables>(() => ({
      baseSiteId: fischer.baseSiteId(),
      channel: fischer.brandId(),
      language: fischer.catalogLocale(),
      accessToken: fischer.user.accessToken(),
      userId: fischer.user.id(),
      ids: productIds.value.join(','),
    })),
    {
      errorPolicy: 'all',
      enabled: computed(() => productIds.value.length > 0),
    },
  );

onGetProductListByIdResult(({ data }) => {
  if (data?.getProductListById) {
    catalogData.value = data.getProductListById;
  }
});

onGetProductListByIdError(onQueryError);

const {
  result: getMultipleBestPrices,
  onResult: onGetMultipleBestPricesResult,
  onError: onGetMultipleBestPricesError,
} = useQuery<GetMultipleBestPricesQuery>(
  GET_MULTIPLE_BEST_PRICES,
  computed<GetMultipleBestPricesQueryVariables>(() => ({
    baseSiteId: fischer.baseSiteId(),
    language: fischer.siteLanguage(),
    userId: fischer.user.id(),
    accessToken: fischer.user.accessToken(),
    queries: productQuery.value,
    productIds: productIds.value,
  })),
  {
    errorPolicy: 'all',
    enabled: computed(() => GET_MULTIPLE_BEST_PRICES.value !== DUMMY && loadPrices.value),
  },
);
onGetMultipleBestPricesResult(({ data, loading }) => {
  if (loading) {
    return;
  }
  if (!data?.getMultipleBestPrices) {
    console.error('Did not receive best prices');
    return;
  }
  data.getMultipleBestPrices.forEach((bestPrice) => {
    if (bestPrice?.productCode) {
      bestPrices.value[bestPrice.productCode] = bestPrice;
    }
  });
  bestPriceLoading.value = false;
});
onGetMultipleBestPricesError((errorValue) => {
  onQueryError(errorValue);
  bestPriceLoading.value = false;
});
const bestPrices = computed(() => {
  return Object.fromEntries(
    (getMultipleBestPrices.value?.getMultipleBestPrices || []).map((bestPrice, idx) => [
      idx,
      bestPrice,
    ]),
  );
});

const { result: getRetailerAvailabilityForIds, onError: onGetRetailerAvailabilityForIdsError } =
  useQuery<GetRetailerAvailabilityForIdsQuery>(
    GET_RETAILER_AVAILABILITY_FOR_IDS,
    computed<GetRetailerAvailabilityForIdsQueryVariables>(() => ({
      baseSiteId: fischer.baseSiteId(),
      productIds: productIds.value,
      useGtins: fischer.useGtins(),
    })),
    {
      enabled: computed(
        () => !IS_SSR && fischer.shop.isRetailConnectCart() && productIds.value.length > 0,
      ),
    },
  );
onGetRetailerAvailabilityForIdsError(onQueryError);

const isIntersected = ref(false);
const onIntersect = () => {
  isIntersected.value = true;
};
watchEffect(() => {
  if (isIntersected.value && catalogData.value?.length) {
    trackViewItemList(catalogData.value);
  }
});
</script>

<style scoped lang="scss">
@import './product-teasers-fi';
</style>
